import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Button,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";

import { salesAPI } from "@/services/api";
import DefaultLayout from "@/layouts/default";
import { useDirectionLanguage } from "@/components/DirectionLanguageContext";
import { formatDate } from "@/utils/dateUtils";

interface SaleItem {
  book_id: string;
  book_name: string;
  quantity: number;
  price_per_book: number;
  subtotal: number;
  discount: number; // Discount percentage
}

interface Sale {
  id: string;
  sale_date: string;
  buyer_name: string;
  buyer_phone: string;
  total_price: number;
  client_id: string;
  invoice_number?: string;
  items: SaleItem[];
  created_at?: string;
  updated_at?: string;
}

const ViewSalePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRtl } = useDirectionLanguage();

  useEffect(() => {
    const fetchSaleDetails = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const response = await salesAPI.getById(id);

        console.log("Sale data received:", response);

        // Ensure we have the correct data structure
        const formattedSale: Sale = {
          id: response.id,
          sale_date: response.sale_date || response.saleDate || "",
          buyer_name: response.buyer_name || response.buyerName || "",
          buyer_phone: response.buyer_phone || response.buyerPhone || "",
          total_price: Number(response.total_price || response.totalPrice) || 0,
          client_id: response.client_id || response.clientId || "",
          invoice_number: response.invoice_number || response.invoiceNumber || "",
          items: Array.isArray(response.items)
            ? response.items.map((item: any) => ({
                book_id: item.book_id || item.bookId,
                book_name: item.book_name || item.bookName,
                quantity: Number(item.quantity) || 1,
                price_per_book:
                  Number(item.price_per_book || item.pricePerBook) || 0,
                discount: Number(item.discount) || 0,
                subtotal:
                  Number(item.subtotal) ||
                  Number(item.price_per_book || item.pricePerBook) *
                    (Number(item.quantity) || 1),
              }))
            : [
                // If items array is not present, create a legacy-compatible single item
                {
                  book_id: response.book_id || "",
                  book_name: response.book_name || "",
                  quantity: Number(response.quantity) || 1,
                  price_per_book: Number(response.price_per_book) || 0,
                  discount: Number(response.discount) || 0,
                  subtotal: Number(response.total_price) || 0,
                },
              ],
          created_at: response.created_at,
          updated_at: response.updated_at,
        };

        setSale(formattedSale);
      } catch (error: any) {
        console.error("Error fetching sale details:", error);
        setError(error.message || t("failed_load_sale_details"));
      } finally {
        setLoading(false);
      }
    };

    fetchSaleDetails();
  }, [id]);

  const calculateDiscountAmount = (price: number, quantity: number, discountPercentage: number): number => {
    const rawSubtotal = price * quantity;
    return (rawSubtotal * discountPercentage) / 100;
  };

  const handleEdit = () => {
    navigate(`/saleEdit/${id}`);
  };

  const handleBack = () => {
    navigate("/sales");
  };

  const generateInvoicePDF = async () => {
    if (!sale) return;

    try {
      // Open the Laravel backend URL in a new tab
      const clientId = localStorage.getItem("clientId") || undefined;
      const backendUrl = await salesAPI.generateInvoice(sale.id, clientId);
      window.open(backendUrl, "_blank");
    } catch (error) {
      console.error("Error generating invoice:", error);
      alert(t("Failed to generate invoice"));
    }
  };

  if (loading) {
    return (
      <DefaultLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <Spinner size="lg" />
        </div>
      </DefaultLayout>
    );
  }

  if (error || !sale) {
    return (
      <DefaultLayout>
        <div className="p-8 text-center">
          <h1 className="text-3xl font-bold text-center mb-8">
            {t("sale_details")}
          </h1>
          <Card>
            <CardBody>
              <p className="text-danger">{error || t("sale_not_found")}</p>
              <Button className="mt-4" color="primary" onClick={handleBack}>
                {t("back_to_sales")}
              </Button>
            </CardBody>
          </Card>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <section className="flex flex-col items-center justify-center py-8">
        <div className="max-w-4xl w-full p-6 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-4">
            {t("sale_details")}
          </h2>

          {/* Sale Summary Information */}
          <div
            className="grid grid-cols-2 gap-x-8 gap-y-4 mb-8"
            dir={isRtl ? "rtl" : "ltr"}
          >
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("buyer_name")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {sale.buyer_name}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("phone")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {sale.buyer_phone || "—"}
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("date")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                <span dir="ltr" style={{ display: 'inline-block', unicodeBidi: 'embed' }}>
                  {formatDate(sale.sale_date)}
                </span>
              </span>
            </div>
            <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
              <span className="font-semibold text-gray-700 dark:text-gray-300">
                {t("items_count")}:
              </span>
              <span className="text-gray-900 dark:text-gray-200">
                {sale.items.length}
              </span>
            </div>
            {sale.invoice_number && (
              <div className="flex flex-col border-b-2 border-gray-300 dark:border-gray-700 p-2">
                <span className="font-semibold text-gray-700 dark:text-gray-300">
                  {t("invoice_number")}:
                </span>
                <span className="text-gray-900 dark:text-gray-200">
                  {sale.invoice_number}
                </span>
              </div>
            )}
          </div>

          {/* Sale Items Table */}
          <div className="mb-6 overflow-x-auto">
            <h3 className="text-xl font-semibold mb-4">{t("items")}</h3>
            <Table aria-label={t("sale_items")} className="min-w-full">
              <TableHeader>
                <TableColumn className="whitespace-nowrap text-center">{t("S.No")}</TableColumn>
                <TableColumn className="whitespace-nowrap">{t("book")}</TableColumn>
                <TableColumn className="whitespace-nowrap text-center">{t("quantity")}</TableColumn>
                <TableColumn className="whitespace-nowrap text-center">{t("price_per_book")}</TableColumn>
                <TableColumn className="whitespace-nowrap text-center">{t("discount")} (%)</TableColumn>
                <TableColumn className="whitespace-nowrap text-center">{t("discount_amount")}</TableColumn>
                <TableColumn className="whitespace-nowrap text-center">{t("subtotal")}</TableColumn>
              </TableHeader>
              <TableBody>
                {sale.items.map((item, index) => (
                  <TableRow key={`${item.book_id}-${index}`}>
                    <TableCell className="text-center">{index + 1}</TableCell>
                    <TableCell>{item.book_name}</TableCell>
                    <TableCell className="text-center">{item.quantity}</TableCell>
                    <TableCell className="text-center">{item.price_per_book.toFixed(2)}</TableCell>
                    <TableCell className="text-center">{item.discount}</TableCell>
                    <TableCell className="text-center">
                      {calculateDiscountAmount(
                        item.price_per_book,
                        item.quantity,
                        item.discount
                      ).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-center">{item.subtotal.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Total Price */}
          <div className="flex flex-col col-span-2 border-b-2 border-gray-300 dark:border-gray-700 p-4 mb-6">
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {t("total_price")}:
            </span>
            <div className="flex items-center justify-center w-full mt-2 p-4 border-2 border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
              <span className="text-gray-900 dark:text-gray-200 text-2xl">
                {(() => {
                  try {
                    return typeof sale.total_price === "number"
                      ? `${sale.total_price.toFixed(2)} ${t("currency")}`
                      : `${Number(sale.total_price).toFixed(2)} ${t("currency")}`;
                  } catch (e) {
                    return `${sale.total_price} ${t("currency")}`;
                  }
                })()}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mt-6">
            <Button color="danger" onClick={handleBack}>
              {t("back_to_sales")}
            </Button>
            <Button color="success" onClick={handleEdit}>
              {t("edit_sale")}
            </Button>
            <Button color="primary" onClick={generateInvoicePDF}>
              {t("generate_invoice")}
            </Button>
          </div>
        </div>
      </section>
    </DefaultLayout>
  );
};

export default ViewSalePage;
